import type { Building } from "building-types/building/buildingTypes";
import type {
  HorizontalEdgePlacementNoExpr,
  HorizontalEdgePlacementIdentity,
  LateralEdgePlacement,
} from "building-types/entities/edgePlacementTypes";
import type { Room } from "building-types/rooms/roomTypes";
import {
  entityDomainDenormalize,
  entityParamFromSpecificWallLayer,
} from "doc/elements/openings/openingWallLayersUtils";
import { roomWidthAtParamFromOutline } from "doc/rooms/roomOutlineIntersection";
import { wallLayerOutlineFromLateralDatum } from "doc/rooms/roomOutlineUtils";
import { line2Eval } from "geom/line2";
import { polyLine2Edge, polyLine2Start, polyLine2End } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist } from "geom/vec2";
import {
  getWallLayerExtents,
  layerMinAndMaxFromSpecificWallLayer,
  wallDatumOffsetAtRoomEdgeParam,
} from "./openings/openingWallLayers";

/**
 * Calculates the min and max offsets on the room edge for an EdgeHostedEntity's
 * horizontal datum. This is meant to be used as a one-off for UI display and
 * translations, so the computed wall layer extents are not cached.
 * TODO: once layer extents are cached globally, this can be removed.
 */

export function edgeHostedEntityGetLayerInfo(
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror">,
  room: Room,
  placement: HorizontalEdgePlacementNoExpr,
): { layerMin: number; layerMax: number; param: number; roomEdge: PolyLine2 } | null {
  if (!building || !room) {
    return null;
  }

  const edgeIndex = room.edgeIds.indexOf(placement.edgeId);
  const outline = room.outline;
  if (!outline || edgeIndex === -1) {
    return null;
  }
  const roomEdge = polyLine2Edge(outline, edgeIndex);

  const [rsx, rsy] = polyLine2Start(roomEdge);
  const [rex, rey] = polyLine2End(roomEdge);
  const edgeLength = vec2Dist(rsx, rsy, rex, rey);

  const { roomIndex, wallMasses, mirror } = building;

  const wallMassSystem = wallMasses[room.level.id];

  const {
    edgeMinTable,
    edgeMaxTable,
    largestInsideOffset: largestOffset,
    mostInwardLayerIntoRoom: mostInwardLayerIntoRoom,
  } = getWallLayerExtents(roomIndex, wallMassSystem, room, edgeIndex);

  const { layerMin, layerMax, param } = entityParamFromSpecificWallLayer({
    placement,
    largestOffset,
    edgeLength,
    mirror,
    mostInwardLayerIntoRoom,
    edgeMinTable,
    edgeMaxTable,
  });

  return { layerMin, layerMax, param, roomEdge };
}

export function edgeHostedEntityGetLayerMinAndMax(
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror">,
  room: Room,
  placement: HorizontalEdgePlacementIdentity,
): { layerMin: number; layerMax: number; roomEdge: PolyLine2 } | null {
  if (!building || !room) {
    return null;
  }

  const edgeIndex = room.edgeIds.indexOf(placement.edgeId);
  const outline = room.outline;
  if (!outline || edgeIndex === -1) {
    return null;
  }
  const roomEdge = polyLine2Edge(outline, edgeIndex);

  const [rsx, rsy] = polyLine2Start(roomEdge);
  const [rex, rey] = polyLine2End(roomEdge);
  const edgeLength = vec2Dist(rsx, rsy, rex, rey);

  const { roomIndex, wallMasses } = building;

  const wallMassSystem = wallMasses[room.level.id];

  const {
    edgeMinTable,
    edgeMaxTable,
    largestInsideOffset: largestOffset,
    mostInwardLayerIntoRoom: mostInwardLayerIntoRoom,
  } = getWallLayerExtents(roomIndex, wallMassSystem, room, edgeIndex);

  const { layerMin, layerMax } = layerMinAndMaxFromSpecificWallLayer({
    placement,
    largestOffset,
    edgeLength,
    mostInwardLayerIntoRoom,
    edgeMinTable,
    edgeMaxTable,
  });

  return { layerMin, layerMax, roomEdge };
}

export function edgeHostedEntityGetLayerEdge(
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror">,
  room: Room,
  placement: HorizontalEdgePlacementIdentity,
): PolyLine2 | null {
  const layerInfo = edgeHostedEntityGetLayerMinAndMax(building, room, placement);
  if (!layerInfo) {
    return null;
  }
  const { layerMin, layerMax, roomEdge } = layerInfo;

  const [rsx, rsy] = polyLine2Start(roomEdge);
  const [rex, rey] = polyLine2End(roomEdge);

  return polyLine2New([
    ...line2Eval(rsx, rsy, rex, rey, layerMin, false),
    ...line2Eval(rsx, rsy, rex, rey, layerMax, false),
  ]);
}

export function edgeHostedEntityGetLayerEdgeLength(
  placement: HorizontalEdgePlacementNoExpr,
  room: Room,
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror">,
): number {
  const layerInfo = edgeHostedEntityGetLayerInfo(building, room, placement);
  if (!layerInfo) {
    return 0;
  }
  let { layerMin, layerMax } = layerInfo;
  if (layerMin === null || layerMax === null) {
    return 0;
  }
  [layerMin, layerMax] = entityDomainDenormalize(placement, [layerMin, layerMax], false);
  return Math.abs(layerMax - layerMin);
}

export function edgeHostedWithoutParamEntityGetLayerEdgeLength(
  placement: HorizontalEdgePlacementIdentity,
  room: Room,
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror">,
): number {
  const layerInfo = edgeHostedEntityGetLayerMinAndMax(building, room, placement);
  if (!layerInfo) {
    return 0;
  }
  const { layerMin, layerMax } = layerInfo;
  if (layerMin === null || layerMax === null) {
    return 0;
  }
  return layerMax - layerMin;
}

export function edgeHostedEntityGetRoomWidthAtParam(
  building: Pick<Building, "roomIndex" | "wallMasses" | "roomOutlineTable" | "mirror">,
  room: Room,
  placement: LateralEdgePlacement,
  edgeIndex: number,
  resolvedParam: number,
): number {
  const { roomOutlineTable } = building;
  const roomOutlines = roomOutlineTable[room.id];
  const wallLayerOutline = wallLayerOutlineFromLateralDatum(room, roomOutlines, placement.lateralDatum);

  const wallMassSystem = building.wallMasses[room.level.id];
  const datumOffset = wallDatumOffsetAtRoomEdgeParam(
    resolvedParam,
    edgeIndex,
    room,
    wallMassSystem,
    placement.lateralDatum,
  );

  return roomWidthAtParamFromOutline(resolvedParam, edgeIndex, room, wallLayerOutline, datumOffset);
}

export function edgeHostedEntityGetLayerEdgeLengthAndRoomWidth(
  placement: HorizontalEdgePlacementNoExpr & LateralEdgePlacement,
  room: Room,
  building: Pick<Building, "roomIndex" | "wallMasses" | "roomOutlineTable" | "mirror">,
): { edgeLength: number; roomWidth: number } | null {
  const layerInfo = edgeHostedEntityGetLayerInfo(building, room, placement);
  if (!layerInfo) {
    return null;
  }
  const { layerMin, layerMax, param: resolvedParam } = layerInfo;
  const edgeIndex = room.edgeIds.indexOf(placement.edgeId);
  const roomWidth = edgeHostedEntityGetRoomWidthAtParam(building, room, placement, edgeIndex, resolvedParam);

  return { edgeLength: layerMax - layerMin, roomWidth };
}
